import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import DoubleTranslatedCustomRichText from '@/components/CustomRichText/DoubleTranslatedCustomRichText';
import AccordionPanel from '@/components/UI/AccordionPanel/AccordionPanel';
import InfoMsg from '@/components/UI/InfoMsg/InfoMsg';
import Spinner from '@/components/UI/Spinner/Spinner';
import { removeParagraphTags, tabLowerCase } from '@/helpers/utils';
import useToastContext from '@/hooks/useToastContext';
import { setHelpDescription } from '@/redux/Help/help.action';
import { getHelpDescription } from '@/redux/Help/help.reducer';
import administrationServices from '@/services/administrationServices';
import { ReduxHelp } from '@/types';

interface DescriptionHelpTextProps {
  helpDescription: ReduxHelp;
  setDescription: (helpDesc: ReduxHelp) => void;
}

const DescriptionHelpText: React.FC<DescriptionHelpTextProps> = (props) => {
  const { helpDescription, setDescription } = props;
  const [originalValues, setOriginalValues] = useState({});
  const { t } = useTranslation();
  const tab = tabLowerCase(window.location.pathname.split('/')[4]);
  const isLoading = !helpDescription.DESCRIPTION_TAB;
  const { addToast } = useToastContext();
  const CLASSIFICATION_RELEVANCE = 'CLASSIFICATION_RELEVANCE';
  const DESCRIPTION_TRIGGER = 'DESCRIPTION_TRIGGER';
  const DESCRIPTION_ISSUE_RESOLUTION_PROCESS = 'DESCRIPTION_ISSUE_RESOLUTION_PROCESS';
  const DESCRIPTION_PROBLEM = 'DESCRIPTION_PROBLEM';
  const DESCRIPTION_INSPECTION = 'DESCRIPTION_INSPECTION';
  const DESCRIPTION_POSSIBLE_CAUSER = 'DESCRIPTION_POSSIBLE_CAUSER';

  const ID_RELEVANCE_CLASSIFICATION = 'relevance-classification';
  const ID_DESCRIPTION_TRIGGER = 'description-trigger';
  const ID_DESCRIPTION_ISSUE_RESOLUTION = 'description-issue-resolution';
  const ID_DESCRIPTION_PROBLEM = 'description-problem';
  const ID_DESCRIPTION_INSPECTION = 'description-inspection';
  const ID_DESCRIPTION_POSSIBLE_CAUSER = 'description-possible-causer';

  useEffect(() => {
    administrationServices.getHelp(tab)
      .then((data) => {
        const values = helpDescription && helpDescription.modified ? helpDescription : data;
        setDescription(values);
        setOriginalValues(values);
      })
      .catch((error) => addToast(error.message, 'ERROR'));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleWysiwygChange = (lang, label, data) => {
    const propValues = { ...helpDescription[label] };
    const isModified = removeParagraphTags(data) !== removeParagraphTags(originalValues[label][lang]);
    const newState = {
      ...helpDescription,
      [label]: {
        ...propValues,
        [lang]: data,
      },
      modified: isModified,
    };

    if (isModified) {
      setDescription(newState as unknown as ReduxHelp);
      setOriginalValues(newState);
    }
  };

  return (
    <>
      {isLoading && <Spinner isVisible isMask />}
      {!isLoading && (
        <>
          <InfoMsg msg={t('administration.helpText.info')} />
          <AccordionPanel title={t('administration.helpText.generalText')}>
            <DoubleTranslatedCustomRichText
              id={ID_RELEVANCE_CLASSIFICATION}
              title={t('helptext')}
              initialValue={helpDescription?.DESCRIPTION_TAB}
              handleInputChange={(lang, newValue) => handleWysiwygChange(lang, CLASSIFICATION_RELEVANCE, newValue)}
            />
          </AccordionPanel>
          <AccordionPanel title={`${t('campaignRequests.title')}: ${t('description.trigger')}`}>
            <DoubleTranslatedCustomRichText
              id={ID_DESCRIPTION_TRIGGER}
              title={t('helptext')}
              initialValue={helpDescription?.DESCRIPTION_TRIGGER}
              handleInputChange={(lang, newValue) => handleWysiwygChange(lang, DESCRIPTION_TRIGGER, newValue)}
            />
          </AccordionPanel>
          <AccordionPanel title={`${t('campaignRequests.title')}: ${t('involvedParties.issueResolutionProcess')}`}>
            <DoubleTranslatedCustomRichText
              id={ID_DESCRIPTION_ISSUE_RESOLUTION}
              title={t('helptext')}
              initialValue={helpDescription?.DESCRIPTION_ISSUE_RESOLUTION_PROCESS}
              handleInputChange={(lang, newValue) => handleWysiwygChange(lang, DESCRIPTION_ISSUE_RESOLUTION_PROCESS, newValue)}
            />
          </AccordionPanel>
          <AccordionPanel title={`${t('campaignRequests.title')}: ${t('description.problem')}`}>
            <DoubleTranslatedCustomRichText
              id={ID_DESCRIPTION_PROBLEM}
              title={t('helptext')}
              initialValue={helpDescription?.DESCRIPTION_PROBLEM}
              handleInputChange={(lang, newValue) => handleWysiwygChange(lang, DESCRIPTION_PROBLEM, newValue)}
            />
          </AccordionPanel>
          <AccordionPanel title={`${t('campaignRequests.title')}: ${t('description.inspection')}`}>
            <DoubleTranslatedCustomRichText
              id={ID_DESCRIPTION_INSPECTION}
              title={t('helptext')}
              initialValue={helpDescription?.DESCRIPTION_INSPECTION}
              handleInputChange={(lang, newValue) => handleWysiwygChange(lang, DESCRIPTION_INSPECTION, newValue)}
            />
          </AccordionPanel>
          <AccordionPanel title={`${t('campaignRequests.title')}: ${t('involvedParties.possibleCauser')}`}>
            <DoubleTranslatedCustomRichText
              id={ID_DESCRIPTION_POSSIBLE_CAUSER}
              title={t('helptext')}
              initialValue={helpDescription?.DESCRIPTION_POSSIBLE_CAUSER}
              handleInputChange={(lang, newValue) => handleWysiwygChange(lang, DESCRIPTION_POSSIBLE_CAUSER, newValue)}
            />
          </AccordionPanel>
        </>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  helpDescription: getHelpDescription(state),
});

const actionCreators = {
  setDescription: setHelpDescription,
};

export default connect(mapStateToProps, actionCreators)(DescriptionHelpText);
